import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Dropdown as AntDropdown, Form } from 'antd';
import classNames from 'classnames';
import styled from 'styled-components';

import { hasAnyCCRCCompetitor, isCommunityCCRC } from 'components/Community/utils';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';

import { CustomCommunityContractTypeFilterContext } from './CustomCommunityContractTypeFilterContext';
import CustomCommunityContractTypeFilterDropdown from './Dropdown/CustomCommunityContractTypeFilterDropdown';
import {
  createContractsByCommunityMap,
  createDefaultCommunityContractSelection,
} from './helpers';
import {
  CommunityContractSelectionType,
  CustomCommunityContractTypeFilterFormValues,
} from './types';

const StyledDropdown = styled(AntDropdown)`
  font-family: var(--font-regular);
  font-size: 12px;
`;

const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 4px;
  border: 1px solid transparent;
  font-size: 12px;
  cursor: pointer;

  color: var(--deep-blue);
  background-color: var(--lighter-gray);

  &.active {
    color: var(--dark-blue);
    background-color: var(--faded-blue);
  }

  &.focused {
    color: var(--dark-blue);
    background-color: var(--faded-blue);
    border: 1px solid var(--button-blue);
  }
`;

const TriggerLabel = styled.span`
  font-family: var(--font-regular);
`;

const TriggerIcon = styled.span`
  display: flex;
  align-items: center;
  margin-left: 6px;
`;

const CustomCommunityContractTypeFilter = () => {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<CustomCommunityContractTypeFilterFormValues>();
  const { community, competitors, viewFilters, setViewFilters } = useCommunityViewContext();

  const shouldRender = useMemo(() => {
    if (!community) {
      return false;
    }
    if (isCommunityCCRC(community?.rental_types)) {
      return false;
    }
    return hasAnyCCRCCompetitor(competitors ?? []);
  }, [community, competitors]);

  const defaultMap = useMemo(
    () =>
      createContractsByCommunityMap({
        community,
        competitors,
        selection: createDefaultCommunityContractSelection(),
      }),
    [community, competitors]
  );

  const committedSelection = useMemo<CommunityContractSelectionType | undefined>(
    () => (viewFilters as any)?.communityContractSelection,
    [viewFilters]
  );

  const initialValues = useMemo<CustomCommunityContractTypeFilterFormValues>(
    () => ({
      overrides: committedSelection?.overrides && Object.keys(committedSelection.overrides).length
        ? { ...defaultMap, ...committedSelection.overrides }
        : { ...defaultMap },
    }),
    [defaultMap, committedSelection]
  );

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  const handleReset = useCallback(() => {
    form.setFieldsValue({ overrides: { ...defaultMap } });
  }, [form, defaultMap]);

  const handleApply = useCallback(
    (values: CustomCommunityContractTypeFilterFormValues) => {
      const overrides: Record<number, number> = {};
      Object.entries(values?.overrides ?? {}).forEach(([key, value]) => {
        const numericKey = Number(key);
        const numericValue = Number(value);
        if (!Number.isNaN(numericKey) && !Number.isNaN(numericValue) && numericValue) {
          overrides[numericKey] = numericValue;
        }
      });

      const nextSelection: CommunityContractSelectionType = { overrides };

      setViewFilters((prev: any) => ({
        ...(prev || {}),
        communityContractSelection: nextSelection,
      }));
      setOpen(false);
    },
    [setViewFilters]
  );

  if (!shouldRender) {
    return null;
  }

  return (
    <StyledDropdown
      trigger={['click']}
      open={open}
      onOpenChange={(visible) => setOpen(visible)}
      placement="bottomLeft"
      dropdownRender={() => (
        <CustomCommunityContractTypeFilterContext.Provider value={{ form, reset: handleReset }}>
          <Form form={form} onFinish={handleApply} initialValues={initialValues}>
            <CustomCommunityContractTypeFilterDropdown />
          </Form>
        </CustomCommunityContractTypeFilterContext.Provider>
      )}
    >
      <TriggerButton
        type="button"
        className={classNames('active', { focused: open })}
        onClick={(event) => event.preventDefault()}
        data-test-id="custom-community-contract-type-filter"
      >
        <TriggerLabel>Contract Type</TriggerLabel>
        <TriggerIcon>
          <img src="/icons/dropdown-caret-down.svg" width={10} height={10} alt="toggle" />
        </TriggerIcon>
      </TriggerButton>
    </StyledDropdown>
  );
};

export default CustomCommunityContractTypeFilter;
